import createClient from "openapi-fetch";
import { ApiProblem, NETWORK_MESSAGE, isProxyBlockResponse, toProblem } from "./problem";
import type { paths } from "./schema.gen";
import { systemParenText } from "../i18n/systemParenText";
import { AUTOMATION_VALUE, CLIENT_AGENT_HEADER, isAutomatedBrowser } from "../lib/automation";

/**
 * Typed client for contracts/openapi.yaml. `schema.gen.ts` is generated (`npm run api:generate`) and
 * committed; CI fails when it drifts from the contract (`npm run api:check`).
 *
 * Same-origin session cookie only: the browser never holds an API key or token (SA-01 §2.4).
 * State-changing requests carry the anti-forgery header the session scheme requires.
 */

/** Anti-forgery cookie read by the SPA. ASSUMPTION: the contract names the header, not the cookie. */
export const XSRF_COOKIE = "XSRF-TOKEN";
export const XSRF_HEADER = "X-XSRF-TOKEN";
const SAFE_METHODS = new Set(["GET", "HEAD", "OPTIONS"]);

function apiBaseUrl(): string {
  // Absolute so the same client works in the browser and in jsdom tests.
  const origin = typeof window === "undefined" ? "http://localhost" : window.location.origin;
  return new URL("/api/v1", origin).href;
}

function readCookie(name: string): string | undefined {
  if (typeof document === "undefined") return undefined;
  const prefix = `${name}=`;
  const part = document.cookie.split("; ").find((c) => c.startsWith(prefix));
  return part ? decodeURIComponent(part.slice(prefix.length)) : undefined;
}

async function isCsrfInvalid(response: Response): Promise<boolean> {
  try {
    const body = (await response.json()) as { code?: unknown; type?: unknown };
    return body.code === "csrf-invalid" || (typeof body.type === "string" && body.type.endsWith("/csrf-invalid"));
  } catch {
    return false;
  }
}

/**
 * Anti-forgery recovery (SA-01 §5.1h R20): a state-changing request refused with 403 `csrf-invalid` refreshes
 * GET /auth/me (which reissues the XSRF-TOKEN cookie) and is retried exactly once with the fresh token.
 * Fetch is resolved at call time so tests and the fixture API can replace it.
 */
async function fetchWithCsrfRetry(request: Request): Promise<Response> {
  const retry = SAFE_METHODS.has(request.method) ? null : request.clone();
  const response = await globalThis.fetch(request);
  if (!retry || response.status !== 403 || !(await isCsrfInvalid(response.clone()))) return response;
  await globalThis.fetch(new Request(new URL("/api/v1/auth/me", apiBaseUrl()), { credentials: "same-origin" }));
  const token = readCookie(XSRF_COOKIE);
  if (token) retry.headers.set(XSRF_HEADER, token);
  return globalThis.fetch(retry);
}

export const apiClient = createClient<paths>({
  baseUrl: apiBaseUrl(),
  credentials: "same-origin",
  // The contract declares array query parameters as style form, explode false: ?type=STORY,TASK
  querySerializer: { array: { style: "form", explode: false } },
  fetch: fetchWithCsrfRetry,
});

apiClient.use({
  onRequest({ request }) {
    // AI attribution (contract 0.10.62): an automated browser says so on every request; the server records its writes as AI.
    if (isAutomatedBrowser()) request.headers.set(CLIENT_AGENT_HEADER, AUTOMATION_VALUE);
    if (!SAFE_METHODS.has(request.method)) {
      const token = readCookie(XSRF_COOKIE);
      if (token) request.headers.set(XSRF_HEADER, token);
    }
    return request;
  },
});

interface ClientResult {
  data?: unknown;
  error?: unknown;
  response: Response;
}

async function settle<R extends ClientResult>(promise: Promise<R>): Promise<R> {
  let result: R;
  try {
    result = await promise;
  } catch {
    throw new ApiProblem(0, undefined, NETWORK_MESSAGE);
  }
  if (!result.response.ok) {
    const problem = toProblem(result.error);
    const contentType = result.response.headers.get("Content-Type");
    // An HTML 403 from the host's network filter (no problem body): its own message, so even a screen that shows
    // `error.message` directly names the likely cause ("System (") instead of "Request failed (403)".
    const proxyBlocked = isProxyBlockResponse(result.response.status, problem, contentType);
    throw new ApiProblem(
      result.response.status,
      problem,
      proxyBlocked ? systemParenText("proxyBlocked", undefined) : undefined,
      result.response.headers.get("Retry-After"),
      result.response.headers.get("Giraffe-Permissions-Revision"),
      contentType,
    );
  }
  return result;
}

/** Awaits a client call, throwing ApiProblem on any non-2xx response. */
export async function call<R extends ClientResult>(promise: Promise<R>): Promise<Exclude<R["data"], undefined>> {
  const result = await settle(promise);
  return result.data as Exclude<R["data"], undefined>;
}

/** As `call`, plus the ETag to send back as If-Match. */
export async function callWithEtag<R extends ClientResult>(
  promise: Promise<R>,
): Promise<{ data: Exclude<R["data"], undefined>; etag: string }> {
  const result = await settle(promise);
  return { data: result.data as Exclude<R["data"], undefined>, etag: result.response.headers.get("ETag") ?? "*" };
}

/** For 204 responses. */
export async function callVoid(promise: Promise<ClientResult>): Promise<void> {
  await settle(promise);
}

/** One key per user intent; a retry of the same intent reuses it (contract: Idempotency-Key). */
export function newIdempotencyKey(): string {
  return crypto.randomUUID();
}

/**
 * Presentations (PRESENT-SPEC-20261028 §3): a plain request to `/api/v1{path}` for screens whose routes are not in the generated
 * schema yet, with the same rules as `apiClient`: same-origin cookie, the anti-forgery header on writes (and the one csrf-invalid
 * retry), the automation header. A non-2xx answer throws ApiProblem; the Response is returned so a caller can read headers.
 */
export async function apiFetch(path: string, init: RequestInit = {}): Promise<Response> {
  const request = new Request(`${apiBaseUrl()}${path}`, { credentials: "same-origin", ...init });
  if (isAutomatedBrowser()) request.headers.set(CLIENT_AGENT_HEADER, AUTOMATION_VALUE);
  if (!SAFE_METHODS.has(request.method)) {
    const token = readCookie(XSRF_COOKIE);
    if (token) request.headers.set(XSRF_HEADER, token);
  }
  let response: Response;
  try {
    response = await fetchWithCsrfRetry(request);
  } catch (error) {
    if (error instanceof DOMException && error.name === "AbortError") throw error;
    throw new ApiProblem(0, undefined, NETWORK_MESSAGE);
  }
  if (!response.ok) {
    const contentType = response.headers.get("Content-Type");
    let body: unknown;
    try {
      body = await response.text();
    } catch {
      body = undefined;
    }
    const problem = toProblem(body);
    throw new ApiProblem(
      response.status,
      problem,
      isProxyBlockResponse(response.status, problem, contentType) ? systemParenText("proxyBlocked", undefined) : undefined,
      response.headers.get("Retry-After"),
      response.headers.get("Giraffe-Permissions-Revision"),
      contentType,
    );
  }
  return response;
}
